import { useMemo, useState } from 'react';
import { Alert } from '@mantine/core';
import { IconChartLine } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { useLatestMetrics } from '../../hooks/useLatestMetrics';
import { MetricTile } from './MetricTile';
import { SkeletonTile } from './SkeletonTile';
import { AllHiddenNotice } from './AllHiddenNotice';
import {
  SortableItem,
  useDashboardDndSensors,
  buildDndAnnouncements,
} from './SortableItem';
import { EmptyState } from '@/components/common/EmptyState';
import { UnifiedManualEntryModal } from '@/components/UnifiedManualEntryModal';
import { useTargetRanges } from '@/hooks/useTargetRanges';
import { useManualEntryParameters } from '@/api/measurements';
import { extractSparklinePoints, type SparklineResponse } from '@/hooks/useSparklineData';
import type { ParameterInfo, TankFeatures } from '@/api/parameters';
import { hasAnyMetricData } from '@/utils/hasAnyMetricData';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTank } from '@/contexts/TankContext';

interface TargetRangeLookup {
  min: number | null;
  max: number | null;
}

// Metric tile configuration for each parameter
export interface TileConfig {
  key: string;  // Parameter key (matches ParameterInfo.key)
  label: string;
  dataKey: keyof LatestMetricsData;  // Key in latestData
  unit: string;
  color: string;
  precision: number;
  rangeKey: string;  // Key in targetRanges lookup
  category: 'sensor' | 'water_test';
}

type LatestMetricsData = {
  temp: { value: number; timestamp: string } | null;
  ph: { value: number; timestamp: string } | null;
  salinity: { value: number; timestamp: string } | null;
  alkalinity: { value: number; timestamp: string } | null;
  ca: { value: number; timestamp: string } | null;
  mg: { value: number; timestamp: string } | null;
  no3: { value: number; timestamp: string } | null;
  po4: { value: number; timestamp: string } | null;
};

// Default tile configurations (default visual order)
export const TILE_CONFIGS: TileConfig[] = [
  { key: 'temp', label: 'Temperature', dataKey: 'temp', unit: '°C', color: 'red', precision: 1, rangeKey: 'temperature', category: 'sensor' },
  { key: 'ph', label: 'pH', dataKey: 'ph', unit: '', color: 'green', precision: 2, rangeKey: 'ph', category: 'sensor' },
  { key: 'salinity', label: 'Salinity', dataKey: 'salinity', unit: 'ppt', color: 'blue', precision: 1, rangeKey: 'salinity', category: 'sensor' },
  { key: 'alkalinity', label: 'Alkalinity', dataKey: 'alkalinity', unit: 'dKH', color: 'cyan', precision: 1, rangeKey: 'alkalinity', category: 'water_test' },
  { key: 'ca', label: 'Calcium', dataKey: 'ca', unit: 'ppm', color: 'grape', precision: 0, rangeKey: 'calcium', category: 'water_test' },
  { key: 'mg', label: 'Magnesium', dataKey: 'mg', unit: 'ppm', color: 'violet', precision: 0, rangeKey: 'magnesium', category: 'water_test' },
  { key: 'no3', label: 'Nitrate', dataKey: 'no3', unit: 'ppm', color: 'orange', precision: 1, rangeKey: 'nitrate', category: 'water_test' },
  { key: 'po4', label: 'Phosphate', dataKey: 'po4', unit: 'ppm', color: 'yellow', precision: 2, rangeKey: 'phosphate', category: 'water_test' },
];

/**
 * Human label for a tile id (e.g. 'temp' → 'Temperature'); falls back to the
 * id itself. Single lookup shared by this grid's dnd announcements and
 * ChartsPage's Add-menu / toast copy.
 */
export function tileLabelFor(id: string): string {
  return TILE_CONFIGS.find(t => t.key === id)?.label ?? id;
}

interface MetricTilesGridProps {
  /** Pre-fetched latest metrics from consolidated dashboard endpoint */
  latestData?: LatestMetricsData;
  /** Pre-fetched target ranges from consolidated dashboard endpoint */
  targetRangesData?: Array<{
    parameterId: string;
    minValue: number | null;
    maxValue: number | null;
  }>;
  /** Loading state from parent */
  isLoading?: boolean;
  /** Error from parent */
  error?: Error | null;
  /** Parameters configuration from unified parameter mapping system (optional) */
  parameters?: ParameterInfo[];
  /** Feature flags from unified parameter mapping system (optional) */
  features?: TankFeatures;
  /** Pre-aggregated sparkline data from /sparkline endpoint */
  sparklineData?: SparklineResponse;
  /** Ordered visible tile ids; defaults to all tiles in TILE_CONFIGS order */
  tileOrder?: string[];
  /** Show drag handles + remove buttons and suspend tile interaction */
  editMode?: boolean;
  /**
   * Called after a drop with the dragged tile's id and the id of the tile it
   * was dropped over — ids, NOT indices. The caller maps both ids to positions
   * in ITS canonical order array (e.g. `order.indexOf(activeId)`): indices
   * into this component's visible grid diverge from the caller's order when a
   * parameter is disabled, so positional indices must never cross this
   * boundary.
   */
  onReorder?: (activeId: string, overId: string) => void;
  /** Called with the tile id when its remove button is pressed in edit mode */
  onRemoveTile?: (id: string) => void;
}

/**
 * Single ordered grid of metric tiles showing current values for key
 * parameters (4 columns on desktop, 2 on mobile).
 *
 * Tiles are filtered by enabled parameters first (a disabled parameter never
 * renders, even if present in tileOrder), then ordered/hidden by tileOrder.
 * In edit mode each tile becomes a dnd-kit sortable item with a drag handle
 * and a remove button. Empty loggable tiles carry a "Log X" CTA (rendered by
 * MetricTile); in edit mode SortableItem applies `inert` to tile content, so
 * the CTA is automatically non-interactive while editing.
 */
export function MetricTilesGrid({
  latestData,
  targetRangesData,
  isLoading: parentLoading,
  error: parentError,
  parameters,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  features: _features, // Reserved for future use - conditionally render charts based on device availability
  sparklineData,
  tileOrder,
  editMode = false,
  onReorder,
  onRemoveTile,
}: MetricTilesGridProps = {}): JSX.Element {
  // ALL hooks live above the early returns (Rules of Hooks)
  const isMobile = useIsMobile();
  const { selectedTank } = useTank();
  const sensors = useDashboardDndSensors();
  const [activeId, setActiveId] = useState<string | null>(null);
  // tileLabelFor is module-scope (stable), so this memo never recomputes; it
  // just keeps the announcements object identity stable across renders.
  const announcements = useMemo(() => buildDndAnnouncements(tileLabelFor), []);
  const [entryModalOpened, { open: openEntryModal, close: closeEntryModal }] =
    useDisclosure(false);

  // Manual-entry parameters drive the per-tile "Log X" CTA: a tile is loggable
  // when its parameter is in this device-independent set (e.g. temp is not).
  const { data: manualEntryParams } = useManualEntryParameters();
  const loggableKeys = useMemo(
    () => new Set((manualEntryParams?.parameters ?? []).map((p) => p.key)),
    [manualEntryParams]
  );

  // Disable hooks when parent provides data OR parent is loading
  const shouldUseHooks = latestData === undefined && parentLoading !== true;

  // Use hooks as fallback when props not provided (backward compatibility)
  const hookResult = useLatestMetrics({ enabled: shouldUseHooks });
  const { data: hookRangesData } = useTargetRanges({ enabled: shouldUseHooks });

  // Use parent data if provided, otherwise use hook data
  const data = latestData ?? hookResult.data;
  const isLoading = parentLoading ?? hookResult.isLoading;
  const error = parentError ?? hookResult.error;
  const rangesData = targetRangesData ?? hookRangesData?.ranges;

  // Convert API ranges to lookup map by parameterId
  // No defaults - if user hasn't configured ranges, tiles show without status colors
  const targetRanges = useMemo(() => {
    const ranges: Record<string, TargetRangeLookup> = {};

    if (rangesData) {
      for (const range of rangesData) {
        ranges[range.parameterId] = {
          min: range.minValue,
          max: range.maxValue,
        };
      }
    }

    return ranges;
  }, [rangesData]);

  // Filter tile configs based on enabled parameters
  // If parameters is not provided, show all tiles (backward compatible)
  const enabledTiles = useMemo(() => {
    if (!parameters) {
      // No parameters config - show all tiles (backward compatible)
      return TILE_CONFIGS;
    }

    // Create a set of enabled parameter keys for efficient lookup
    const enabledKeys = new Set(
      parameters
        .filter(p => p.is_enabled)
        .map(p => p.key)
    );

    // Filter tiles to only show enabled parameters
    return TILE_CONFIGS.filter(tile => enabledKeys.has(tile.key));
  }, [parameters]);

  // Intersect tileOrder with the enabled subset: parameter enablement wins,
  // so a disabled parameter never renders even if present in tileOrder.
  const orderedTiles = useMemo(() => {
    const order = tileOrder ?? TILE_CONFIGS.map(t => t.key);
    return order
      .map(id => enabledTiles.find(t => t.key === id))
      .filter((t): t is TileConfig => t !== undefined);
  }, [tileOrder, enabledTiles]);

  // Pre-compute sparkline data only for visible tiles when input data changes
  const sparklineDataMap = useMemo(() => {
    if (!sparklineData) return undefined;
    const map: Record<string, Array<{ value: number | null }>> = {};
    for (const config of orderedTiles) {
      map[config.key] = extractSparklinePoints(sparklineData, config.key);
    }
    return map;
  }, [sparklineData, orderedTiles]);

  // One grid definition shared by the skeleton and loaded states, so the
  // column count never reflows when data arrives. gridAutoRows: 1fr equalizes
  // every row to the tallest tile, so tiles with less content (no sparkline,
  // unwrapped timestamp) don't leave ragged gaps below themselves.
  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
    gridAutoRows: '1fr',
    gap: isMobile ? 'var(--mantine-spacing-xs)' : 'var(--mantine-spacing-md)',
  };

  if (error) {
    return (
      <Alert color="red" title="Error loading metrics">
        Failed to load latest metrics. Please try refreshing.
      </Alert>
    );
  }

  // With no tank selected the tank-scoped queries are disabled and never
  // resolve, so `data` stays undefined and the skeleton grid below would render
  // forever. Show an inline empty state instead. (Task 19 makes this the hero.)
  if (!selectedTank) {
    return (
      <EmptyState
        variant="inline"
        icon={IconChartLine}
        title="Your dashboard fills in as data arrives."
        data-testid="dashboard-empty-state"
      />
    );
  }

  // Show skeleton grid while loading — but only when there are tiles to load
  // into. If the user hid every tile (tileOrder provided and empty), skip the
  // skeletons; the all-hidden empty state renders once loading finishes.
  if (isLoading || !data) {
    if (tileOrder !== undefined && orderedTiles.length === 0) {
      return <></>;
    }

    const skeletonCount = orderedTiles.length || 8;

    return (
      <div style={gridStyle}>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <SkeletonTile key={`skeleton-${i}`} />
        ))}
      </div>
    );
  }

  // If no tiles are enabled, guide the user rather than showing a passive
  // dead-end alert (design 3.4.6) — but WHICH guidance depends on whether the
  // tank has any data at all.
  //
  // Enabled tiles come from tank_parameters, which is a DEVICE mapping. A
  // manual-only tank never gets a row there, so it sits in this branch forever
  // even once it is full of logged water tests: telling that user their
  // "dashboard fills in as data arrives" and to log a first test contradicts
  // the populated charts rendered right below this grid.
  //
  // The has-data signal is free. `data` is the latest-metrics payload (already
  // resolved — the loading guard above returned otherwise), and a non-null
  // entry means measurements exist, manual or otherwise. hasAnyMetricData is
  // the SAME rule useGettingStarted's `first-data` item reads (which is what
  // ChartsPage reads for isZeroData), so the two never disagree, and it costs
  // no extra query.
  if (enabledTiles.length === 0) {
    const hasAnyData = hasAnyMetricData(data);

    if (hasAnyData) {
      return (
        <EmptyState
          variant="inline"
          icon={IconChartLine}
          title="Metric tiles appear when a device is connected"
          description="Your logged tests are charted below — connect a device to unlock live tiles."
          primaryAction={{ label: 'Connect a device', to: '/devices' }}
          data-testid="no-parameters-empty-state"
        />
      );
    }

    return (
      <>
        <EmptyState
          variant="inline"
          icon={IconChartLine}
          title="Your dashboard fills in as data arrives."
          description="Log your first water test — it takes about 30 seconds — or connect a device to pull in your history."
          primaryAction={{ label: 'Log a water test', onClick: openEntryModal }}
          secondaryAction={{ label: 'Connect a device', to: '/devices' }}
          data-testid="no-parameters-empty-state"
        />
        <UnifiedManualEntryModal
          opened={entryModalOpened}
          onClose={closeEntryModal}
          initialType="water-test"
        />
      </>
    );
  }

  // Parameters are enabled but the user hid every tile
  if (orderedTiles.length === 0) {
    return <AllHiddenNotice itemNoun="tiles" />;
  }

  // Render a tile from config (key lives on SortableItem / DragOverlay)
  const renderTile = (config: TileConfig): JSX.Element => {
    const metricData = data[config.dataKey];
    return (
      <MetricTile
        label={config.label}
        value={metricData?.value ?? null}
        unit={config.unit}
        lastUpdated={metricData?.timestamp ?? ''}
        color={config.color}
        precision={config.precision}
        targetMin={targetRanges[config.rangeKey]?.min ?? undefined}
        targetMax={targetRanges[config.rangeKey]?.max ?? undefined}
        sparklineData={sparklineDataMap?.[config.key]}
        parameterKey={config.key}
        loggable={loggableKeys.has(config.key)}
      />
    );
  };

  const idsInOrder = orderedTiles.map(t => t.key);
  const activeTile = activeId ? orderedTiles.find(t => t.key === activeId) : undefined;

  const handleDragStart = (event: DragStartEvent): void => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorder?.(String(active.id), String(over.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      accessibility={{ announcements }}
      onDragStart={handleDragStart}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={idsInOrder} strategy={rectSortingStrategy}>
        <div style={gridStyle}>
          {orderedTiles.map(config => (
            <SortableItem
              key={config.key}
              id={config.key}
              editMode={editMode}
              onRemove={() => onRemoveTile?.(config.key)}
              removeLabel={config.label}
            >
              {renderTile(config)}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
      <DragOverlay>
        {activeTile ? (
          <div
            style={{
              transform: 'scale(1.03)',
              border: '1px solid var(--mantine-color-gray-4)',
              borderRadius: 'var(--mantine-radius-md)',
            }}
          >
            {renderTile(activeTile)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
